import React from 'react'
import * as Yup from 'yup'
import {
  ButtonBox,
  StyledDialog,
  DialogBox,
  DialogText,
  DialogTitle,
  StyledCloseIcon,
  CheckboxBox,
  PaymentMethod,
} from './styles'
import {Button} from '../button'

import Challenge30Logo from '../../images/study-challenge-logo.svg'
import SuccessIcon from '../../images/icons/success-icon.svg'

import {Form, Formik} from 'formik'
import {Box, Checkbox, FormControlLabel, Typography} from '@mui/material'
import {useSelector} from 'react-redux'
import {selectMember, selectMemberAccess} from '../../redux/members'
import SelectField from '../SelectField'
import {Course} from '../../redux/courses'
import TextFieldLabelAbove from '../TextFieldLabelAbove'
import {Link, useNavigate} from 'react-router-dom'
import {Product} from '../../redux/checkout'
import {CardDetail} from '../../redux/organizations'
import EditIcon from '../../images/icons/pencil-icon.svg'
import {useAuth} from '../AuthProvider'
import LogInButton from '../LogInButton/logInButton'

type Props = {
  open: boolean
  setOpen: (value: boolean) => void
  onChangeExamScheduled: (value: string) => void
  courses?: Course[]
  courseId?: string
  onSubmit: (values: FormProps) => void
  product?: Product
  isSubmitting: boolean
  onChangeCurrentStep: (value: 'form' | 'payment' | 'success') => void
  currentStep: 'form' | 'payment' | 'success'
  cardDetails?: CardDetail
  isGroupAccount?: boolean
}

export type FormProps = {
  name: string
  email: string
  division?: string
  scheduled?: string
  checked?: boolean
}

const View = ({
  open,
  setOpen,
  onChangeExamScheduled,
  courses,
  courseId,
  onSubmit,
  product,
  isSubmitting,
  onChangeCurrentStep,
  currentStep,
  cardDetails,
  isGroupAccount,
}: Props) => {
  const navigate = useNavigate()
  const auth = useAuth()

  const member = useSelector(selectMember)
  const access = useSelector(selectMemberAccess)

  const hasAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.study_guides?.value) === 1
      : false

  const isPremium = member?.groupId !== 1 && (
    ((member?.accessLevel === 'premium' || !hasAccess) &&
      member?.accessLevel !== null &&
      !['expert', 'pro'].includes(member?.accessLevel ?? '')) ||
    member?.stripeSubscriptionStatus !== 'active')

  const handleClose = () => {
    setOpen(false)
    onChangeCurrentStep('form')
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    email: Yup.string().required('Required'),
    division: Yup.string().required('Required'),
    scheduled: Yup.string().required('Required'),
  })

  const initialValues: FormProps = {
    name: `${member?.firstName} ${member?.lastName}`,
    email: `${member?.email}`,
    division: courseId ?? '',
    scheduled: '',
    checked: false,
  }

  const handleSubmit = (values: FormProps) => {
    onSubmit(values)
  }
  const handleClick = () => {
    if (isGroupAccount) return

    if (isPremium) {
      navigate('/profile/settings/manage', {replace: true})
      setOpen(false)
    } else {
      onChangeCurrentStep('payment')
    }
  }

  const courseOptions = (courses ?? []).map(course => {
    return {value: course.id, name: course.title}
  })

  return (
    <StyledDialog maxWidth={'md'} onClose={handleClose} open={open}>
      <DialogBox>
        <DialogTitle>
          {currentStep === 'payment' ? (
            <>Payment Confirmation</>
          ) : (
            <>
              {currentStep === 'success' ? (
                <img src={SuccessIcon} alt="Success" />
              ) : (
                <img src={Challenge30Logo} alt="30 Day study challenge" />
              )}
            </>
          )}
          <StyledCloseIcon
            onClick={handleClose}
            className="x-button"
            style={{
              height: 32,
              width: 32,
              cursor: 'pointer',
            }}
          />
        </DialogTitle>
        {auth.isAuthenticated ? (
          <>
            <DialogText>
              {currentStep === 'payment' ? (
                <>
                  <Typography variant="body1">
                    <b>Selected Plan:</b> 30-Day Study Challenge
                  </Typography>
                  <Typography variant="body1" marginTop={2}>
                    <b>One-Time Payment:</b> $
                    {product?.amount ? product?.amount / 100 : 0}
                  </Typography>
                  <Typography variant="body1" marginTop={2}>
                    <b>Estimated Tax:</b> $
                    {product?.tax.absolute ? product?.tax.absolute / 100 : 0}
                  </Typography>
                  <Typography variant="body1" marginTop={2}>
                    <b>Total amount:</b> $
                    {product?.amount
                      ? (product?.amount + product?.tax.absolute) / 100
                      : 0}
                  </Typography>
                  <PaymentMethod variant="body1" marginTop={2}>
                    <b>Payment Method:</b>{' '}
                    <em>[Card ending in ****{cardDetails?.lastFour}]</em>{' '}
                    <Link
                      to={'/profile/settings/billing'}
                      onClick={handleClose}
                    >
                      Update <img src={EditIcon} alt="Edit credit card" />
                    </Link>
                  </PaymentMethod>
                  <Typography variant="body2" marginTop={2}></Typography>
                </>
              ) : (
                <>
                  {!isPremium ? (
                    <>
                      {isGroupAccount ? (
                        <>
                          {currentStep === 'success' ? (
                            <div style={{textAlign: 'center'}}>
                              <span>Participation Confirmed</span>
                              <p>
                                You've successfully joined the 30 day study
                                challenge!
                              </p>
                              <p>
                                Keep an eye on your email for more details and
                                next steps.
                              </p>
                            </div>
                          ) : (
                            <>
                              As a firm member, you get{' '}
                              <b>
                                free access to the structure, resources, and
                                community support you need
                              </b>{' '}
                              to confidently pass your next ARE exam.
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {currentStep === 'success' ? (
                            <div style={{textAlign: 'center'}}>
                              <span>Payment Successful</span>
                              <p>
                                Your payment has been processed successfully.
                              </p>
                              <p>
                                You'll receive a confirmation email with your
                                invoice shortly.
                              </p>
                            </div>
                          ) : (
                            <>
                              <b>
                                Join the 30-Day Studying Challenge and stay on
                                track with your exam prep!
                              </b>{' '}
                              For $20, you'll get access to the structure,
                              resources, and community support you need to
                              confidently pass your next ARE exam.
                            </>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <span>
                        Upgrade to Pro or Expert to join the 30-Day Studying
                        Challenge!
                      </span>
                      For $20 get access to the structure, resources, &
                      community support you need to confidently pass your next
                      ARE exam.
                    </>
                  )}
                </>
              )}
            </DialogText>
            {!isPremium && currentStep !== 'success' && (
              <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                enableReinitialize={true}
              >
                {formik => (
                  <Form>
                    {currentStep === 'payment' ? (
                      <>
                        <CheckboxBox marginTop={2}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formik.values.checked}
                                name="checked"
                                onChange={() =>
                                  formik.setFieldValue(
                                    'checked',
                                    !formik.values.checked,
                                  )
                                }
                              />
                            }
                            label={
                              <>
                                By completing this purchase, you agree to the{' '}
                                <a
                                  href="https://www.blackspectacles.com/terms-and-conditions"
                                  target="_blank"
                                >
                                  Terms & Conditions
                                </a>{' '}
                                and{' '}
                                <a
                                  href="https://www.blackspectacles.com/privacy-policy"
                                  target="_blank"
                                >
                                  Privacy Policy
                                </a>
                                .
                              </>
                            }
                          />
                        </CheckboxBox>
                      </>
                    ) : (
                      <>
                        <Box marginTop={2}>
                          <TextFieldLabelAbove
                            name="name"
                            variant="filled"
                            disabled={true}
                            fullWidth
                            style={{height: '48px'}}
                            displayTitle={'Name'}
                            placeholder="Type in Person Name"
                          />
                        </Box>

                        <Box marginTop={2}>
                          <TextFieldLabelAbove
                            name="email"
                            variant="filled"
                            disabled={true}
                            fullWidth
                            displayTitle={'Email'}
                            style={{height: '48px'}}
                            placeholder="Type in Person Email"
                          />
                        </Box>

                        <Box marginTop={2}>
                          <SelectField
                            name="division"
                            options={courseOptions}
                            fullWidth
                            variant="filled"
                            label="Which ARE division are you studying for?"
                          />
                        </Box>

                        <Box marginTop={2}>
                          <SelectField
                            name="scheduled"
                            options={[
                              {value: 'yes', name: 'Yes'},
                              {value: 'no', name: 'No'},
                            ]}
                            fullWidth
                            variant="filled"
                            label="Have you scheduled your exam?"
                            blank="Yes/No"
                            onChange={(value: string) => {
                              onChangeExamScheduled(value)
                            }}
                          />
                        </Box>
                      </>
                    )}
                    <ButtonBox $isCentered={currentStep === 'payment'}>
                      {currentStep === 'payment' ? (
                        <Button
                          children={'Complete Purchase'}
                          color={'secondary'}
                          size={'large'}
                          type="submit"
                          disabled={!formik.values.checked || isSubmitting}
                        />
                      ) : (
                        <>
                          <Button
                            children={
                              isGroupAccount ? 'Participate Now' : 'Continue'
                            }
                            color={'secondary'}
                            size={'large'}
                            type={isGroupAccount ? 'submit' : 'button'}
                            onClick={handleClick}
                          />
                        </>
                      )}
                    </ButtonBox>
                  </Form>
                )}
              </Formik>
            )}
            {isPremium && (
              <ButtonBox>
                <Button
                  children={'Upgrade & Join'}
                  color={'secondary'}
                  size={'large'}
                  type={'button'}
                  onClick={handleClick}
                />
              </ButtonBox>
            )}
            {currentStep === 'success' && (
              <ButtonBox>
                <Button
                  children={'Join the Community Now'}
                  color={'secondary'}
                  size={'large'}
                  type={'button'}
                  onClick={() =>
                    window.open(
                      'https://community.blackspectacles.com/t/join-the-30-day-study-challenge-may-edition/6786',
                    )
                  }
                />
              </ButtonBox>
            )}
          </>
        ) : (
          <>
            <DialogText>
              <b>
                Join the 30-Day Studying Challenge and stay on track with your
                exam prep!
              </b>{' '}
              For $20, you'll get access to the structure, resources, and
              community support you need to confidently pass your next ARE exam.
            </DialogText>
            <ButtonBox>
              <LogInButton />
            </ButtonBox>
          </>
        )}
      </DialogBox>
    </StyledDialog>
  )
}

export default View
